/**
 * IntensityReportPageV2 — redesigned Intensity Report page.
 *
 * Two-section layout under the PageHeader:
 *   01 New report — date range pickers + Generate (coral)
 *   02 Past reports — LibTable of generation jobs with status pill,
 *      created/completed/expires timestamps, and per-row actions
 *      (Download / Extend / error message / in-progress text)
 *
 * Same async-job polling as v1: when a report's status is pending
 * or processing, a 5s interval poll fires checkIntensityReportStatus
 * until the job completes / fails / expires.
 *
 * Uses the redesign-native DatePicker (token-themed, dark-mode
 * correct) instead of MUI's.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import {
  exportIntensityDataCSV,
  fetchIntensityReports,
  checkIntensityReportStatus,
  downloadIntensityReport,
  extendIntensityReportExpiration,
} from '../../../../actions/exportCSVActions';
import {
  PageHeader,
  Button,
  LibTable,
  StatusPill,
  cn,
} from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  DatePicker,
} from '../../../redesign/edit';

const DownloadIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const STATUS_TONE = {
  pending:    'amber',
  processing: 'blue',
  completed:  'green',
  failed:     'coral',
  expired:    'gray',
};

function formatTimestamp(iso) {
  if (!iso) return 'N/A';
  try {
    return format(new Date(iso), 'MMM dd, yyyy HH:mm');
  } catch {
    return iso;
  }
}

export default function IntensityReportPageV2() {
  const dispatch = useDispatch();

  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [reports, setReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const pollingIntervalsRef = useRef({});

  const loadReports = async (preserveOptimistic = false) => {
    setIsLoadingReports(true);
    try {
      const data = await dispatch(fetchIntensityReports());
      if (Array.isArray(data)) {
        setReports((prev) => {
          if (preserveOptimistic) {
            const serverIds = new Set(data.map((r) => r.job_id));
            const optimistic = prev.filter((r) => !serverIds.has(r.job_id));
            return [...data, ...optimistic];
          }
          return data;
        });
        data.forEach((r) => {
          if (
            (r.status === 'pending' || r.status === 'processing') &&
            !pollingIntervalsRef.current[r.job_id]
          ) {
            startPolling(r.job_id);
          }
        });
      }
    } catch (e) {
      console.error('Failed to load reports:', e);
    } finally {
      setIsLoadingReports(false);
    }
  };

  useEffect(() => {
    loadReports();
    // Capture the ref value at effect-setup time so the cleanup
    // can iterate the same intervals object even if the ref shifts
    // later. Plain mount-only effect — the project doesn't run the
    // react-hooks/exhaustive-deps rule, so the [] is fine.
    const intervals = pollingIntervalsRef.current;
    return () => {
      Object.values(intervals).forEach((id) => clearInterval(id));
    };
  }, []);

  const startPolling = (jobId) => {
    if (pollingIntervalsRef.current[jobId]) return;
    const interval = setInterval(async () => {
      try {
        const job = await dispatch(checkIntensityReportStatus(jobId));
        if (job) {
          setReports((prev) =>
            prev.map((r) => (r.job_id === jobId ? job : r)),
          );
          if (['completed', 'failed', 'expired'].includes(job.status)) {
            stopPolling(jobId);
          }
        }
      } catch (e) {
        console.error(`Failed status check for ${jobId}:`, e);
        stopPolling(jobId);
      }
    }, 5000);
    pollingIntervalsRef.current[jobId] = interval;
  };

  const stopPolling = (jobId) => {
    if (pollingIntervalsRef.current[jobId]) {
      clearInterval(pollingIntervalsRef.current[jobId]);
      delete pollingIntervalsRef.current[jobId];
    }
  };

  const handleGenerate = async () => {
    try {
      const jobId = await dispatch(
        exportIntensityDataCSV({
          from_date: format(fromDate, 'yyyy-MM-dd'),
          to_date: format(toDate, 'yyyy-MM-dd'),
        }),
      );
      if (jobId) {
        const newJob = {
          job_id: jobId,
          from_date: format(fromDate, 'yyyy-MM-dd'),
          to_date: format(toDate, 'yyyy-MM-dd'),
          status: 'pending',
          created_at: new Date().toISOString(),
          completed_at: null,
          file_name: null,
          error_message: null,
        };
        setReports((prev) => [newJob, ...prev]);
        startPolling(jobId);
        Swal.fire({
          icon: 'success',
          title: 'Report generation started',
          timer: 1500,
          showConfirmButton: false,
        });
        loadReports(true);
      }
    } catch (e) {
      console.error('Failed to create report:', e);
      Swal.fire({
        icon: 'error',
        title: 'Failed to start report generation',
      });
    }
  };

  const handleDownload = (jobId, fileName) => {
    dispatch(downloadIntensityReport(jobId, fileName));
  };

  const handleExtend = async (jobId) => {
    try {
      const updated = await dispatch(extendIntensityReportExpiration(jobId, 30));
      if (updated) {
        setReports((prev) =>
          prev.map((r) => (r.job_id === jobId ? updated : r)),
        );
        Swal.fire({
          icon: 'success',
          title: 'Expiration extended 30 days',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (e) {
      console.error('Failed to extend expiration:', e);
      Swal.fire({
        icon: 'error',
        title: 'Failed to extend expiration',
      });
    }
  };

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Reports"
        title="Intensity Report"
      />

      <div style={{ padding: '0 28px 60px', maxWidth: 1080 }}>
        <FormSection title="New report" num="01">
          <FieldRow>
            <FieldBlock label="From date" htmlFor="f-int-from" noMargin>
              <DatePicker value={fromDate} onChange={setFromDate} />
            </FieldBlock>
            <FieldBlock label="To date" htmlFor="f-int-to" noMargin>
              <DatePicker value={toDate} onChange={setToDate} />
            </FieldBlock>
          </FieldRow>
          <div style={{ marginTop: 14 }}>
            <Button
              variant="coral"
              leadingIcon={PlusSign}
              onClick={handleGenerate}
            >
              Generate report
            </Button>
          </div>
        </FormSection>

        <FormSection
          title="Past reports"
          num="02"
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              {reports.length.toLocaleString()} total
            </span>
          }
        >
          {isLoadingReports && reports.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
            >
              Loading reports…
            </div>
          ) : reports.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
            >
              No reports yet. Generate one to get started.
            </div>
          ) : (
            <LibTable
              fixed
              className="border border-sand"
              style={{ borderRadius: 12, overflow: 'hidden' }}
            >
              <colgroup>
                {/* date range */}
                <col style={{ width: 200 }} />
                {/* status */}
                <col style={{ width: 130 }} />
                {/* created */}
                <col style={{ width: 160 }} />
                {/* completed */}
                <col style={{ width: 160 }} />
                {/* expires */}
                <col style={{ width: 160 }} />
                {/* actions */}
                <col />
              </colgroup>
              <LibTable.Head>
                <tr>
                  <LibTable.Th>Date range</LibTable.Th>
                  <LibTable.CenterTh>Status</LibTable.CenterTh>
                  <LibTable.Th>Created</LibTable.Th>
                  <LibTable.Th>Completed</LibTable.Th>
                  <LibTable.Th>Expires</LibTable.Th>
                  <LibTable.Th>Actions</LibTable.Th>
                </tr>
              </LibTable.Head>
              <tbody>
                {reports.map((r) => {
                  const expiringSoon = r.is_expiring_soon || false;
                  const tone = STATUS_TONE[r.status] || 'gray';
                  return (
                    <LibTable.Row
                      key={r.job_id}
                      className={cn(expiringSoon && 'data-[expiring=true]:bg-[#FFF3EE]')}
                      data-expiring={expiringSoon ? 'true' : 'false'}
                    >
                      <LibTable.Td>
                        <span className="font-mono text-navy" style={{ fontSize: 12 }}>
                          {r.from_date} → {r.to_date}
                        </span>
                      </LibTable.Td>
                      <LibTable.CenterTd>
                        <StatusPill tone={tone}>{r.status}</StatusPill>
                      </LibTable.CenterTd>
                      <LibTable.Td>
                        <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                          {formatTimestamp(r.created_at)}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                          {r.completed_at ? formatTimestamp(r.completed_at) : 'N/A'}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        <span
                          className={cn(
                            'font-mono',
                            expiringSoon ? 'text-coral' : 'text-text-muted',
                          )}
                          style={{ fontSize: 11.5, whiteSpace: 'nowrap', fontWeight: expiringSoon ? 600 : 400 }}
                        >
                          {r.expires_at ? formatTimestamp(r.expires_at) : 'N/A'}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        {r.status === 'completed' ? (
                          <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                            <Button
                              variant="ghost"
                              leadingIcon={DownloadIcon}
                              onClick={() => handleDownload(r.job_id, r.file_name)}
                            >
                              Download
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => handleExtend(r.job_id)}
                            >
                              Extend
                            </Button>
                          </div>
                        ) : r.status === 'failed' ? (
                          <span className="text-coral" style={{ fontSize: 12 }}>
                            {r.error_message || 'Generation failed'}
                          </span>
                        ) : r.status === 'expired' ? (
                          <span className="text-text-muted italic" style={{ fontSize: 12 }}>
                            Expired
                          </span>
                        ) : (
                          <span className="text-text-muted italic" style={{ fontSize: 12 }}>
                            In progress…
                          </span>
                        )}
                      </LibTable.Td>
                    </LibTable.Row>
                  );
                })}
              </tbody>
            </LibTable>
          )}
        </FormSection>
      </div>
    </div>
  );
}
